import React from 'react';
import { Helmet } from 'react-helmet-async';
import QuizShell from '../../components/quiz/QuizShell';
import type { QuizConfig } from '../../components/quiz/QuizShell';

const config: QuizConfig = {
  niche: 'bladder-urgency',
  quizSlug: 'bladder-urgency',
  pageTitle: 'Do you find yourself rushing to the bathroom more than you\'d like?',
  pageSubtitle: 'Answer 4 quick questions to understand your urgency pattern and how hypnotherapy can help.',
  questions: [
    {
      id: 'frequency',
      question: 'How often do you experience sudden, strong urges to use the toilet?',
      introText: 'This is more common than many people realise.',
      options: [
        { label: 'A few times a day', points: 1 },
        { label: 'Several times a day', points: 2 },
        { label: 'Very frequently throughout the day', points: 3 },
      ],
    },
    {
      id: 'urgency-level',
      question: 'How much notice do you typically have before you need to use the toilet urgently?',
      options: [
        { label: 'Usually enough time to get there calmly', points: 1 },
        { label: 'I need to move quickly', points: 2 },
        { label: 'I have to rush immediately', points: 3 },
      ],
    },
    {
      id: 'avoidance',
      question: 'Have you changed any activities or routines to manage toilet urgency?',
      options: [
        { label: 'Occasionally — I plan slightly more', points: 1 },
        { label: 'Often — I make sure to know where toilets are', points: 2 },
        { label: 'Frequently — it significantly affects what I do and where I go', points: 3 },
      ],
    },
    {
      id: 'confidence-impact',
      question: 'How much does bladder urgency affect your confidence or peace of mind?',
      options: [
        { label: 'A little — it\'s occasionally frustrating', points: 1 },
        { label: 'Moderately — I think about it more than I\'d like', points: 2 },
        { label: 'Significantly — it\'s on my mind a lot', points: 3 },
      ],
    },
  ],
  tiers: [
    {
      name: 'Mild',
      tierNumber: 1,
      minScore: 4,
      blurb:
        'Your results suggest mild urgency that has begun to limit what you feel comfortable doing. This is more common than most people realise, and hypnotherapy can help retrain the urgency signal so you feel more in control.',
    },
    {
      name: 'Moderate',
      tierNumber: 2,
      minScore: 7,
      blurb:
        'Your results show moderate urgency affecting daily life in a real way. The pattern of avoidance and planning around toilet access is exhausting. Hypnotherapy works with the nervous system signals driving the urgency — not just the symptoms.',
    },
    {
      name: 'Significant',
      tierNumber: 3,
      minScore: 10,
      blurb:
        'Your results indicate significant urgency that is having a substantial impact on your freedom and confidence. This is a recognised pattern that responds well to hypnotherapy. Many clients see meaningful improvement within a few sessions.',
    },
  ],
};

const BladderUrgencyQuiz: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Bladder Urgency Quiz | Sharon Clare Hypnotherapy</title>
        <meta
          name="description"
          content="Take our short quiz to understand your bladder urgency pattern and find out how hypnotherapy can help."
        />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/quiz/bladder-urgency/" />
      </Helmet>
      <QuizShell config={config} />
    </>
  );
};

export default BladderUrgencyQuiz;
